import { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, Stack, Typography, Switch, Button, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert, TextField, Divider,
} from '@mui/material';
import {
  Tune as SettingsIcon, Send as SubmitIcon, Rule as RuleIcon,
  Info as InfoIcon, Save as SaveIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ═══════════════════════════════════════════════════════════════════
//  Collection Settings.
//
//  Two decisions a school makes once and then lives with, both of which
//  were previously hard-coded or reachable only through the API:
//
//    HANDING IN. Must every learner be answered for before a round can
//    be submitted? Safer on. Off for a school that would rather bank
//    what was collected than hold a class's cash for one absentee.
//
//    CHARGING. Does a day become a debt when the learner was absent, or
//    came and paid nothing? This is the difference between a compulsory
//    daily fee and a meal you only pay for when you eat it, and it has
//    to be settable per collection because one school runs both.
//
//  Nothing here recomputes history. Every switch is forward-looking: a
//  day already marked keeps the charge it was given, because that money
//  may already have been collected, receipted and banked.
// ═══════════════════════════════════════════════════════════════════

const prettyType = (t) => String(t || '')
  .replace(/_+$/, '').replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

// The two shapes almost every school is actually running, so a bursar
// can pick the model rather than reason about four switches.
const PRESETS = {
  used:       { charge_when_absent: 0, charge_when_unpaid: 0 },
  compulsory: { charge_when_absent: 1, charge_when_unpaid: 1 },
};
const presetOf = (r) =>
  r.charge_when_absent && r.charge_when_unpaid ? 'compulsory'
  : !r.charge_when_absent && !r.charge_when_unpaid ? 'used'
  : 'mixed';

function SettingRow({ label, hint, checked, onChange, disabled }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start"
      sx={{ py: 1.1, borderBottom: '1px solid #f1f5f9' }}>
      <Switch size="small" checked={!!checked} disabled={disabled}
        onChange={(e) => onChange(e.target.checked ? 1 : 0)} sx={{ mt: -0.3 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b', lineHeight: 1.6 }}>
          {hint}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function CollectionSettingsPage() {
  const [types, setTypes] = useState([]);
  const [rules, setRules] = useState({});
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingType, setSavingType] = useState('');
  const [savingHandIn, setSavingHandIn] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // The income types that actually run daily are the ones that
      // carry subscriptions — offering the school's whole chart of
      // income here would be a page of rules nobody uses.
      const [s, subs] = await Promise.all([
        API.get('/daily-subscriptions/settings'),
        API.get('/daily-subscriptions', { params: { limit: 1 } }).catch(() => ({ data: [] })),
      ]);
      setSettings(s.data || {});

      let list = [];
      try {
        const it = await API.get('/collections/income-types');
        list = [...new Set((it.data || []).map(t => t.type).filter(Boolean))];
      } catch { list = []; }

      // Narrow to the ones with a daily subscription behind them, when
      // we can tell; otherwise show everything rather than nothing.
      const used = new Set(
        (Array.isArray(subs.data) ? subs.data : subs.data?.rows || [])
          .map(r => r.income_type).filter(Boolean));
      const shown = used.size ? list.filter(t => used.has(t)) : list;
      setTypes(shown);

      if (shown.length) {
        const r = await API.get('/daily-subscriptions/charge-rules',
          { params: { types: shown.join(',') } });
        setRules(r.data || {});
      } else setRules({});
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load the settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveHandIn = async (value) => {
    setSavingHandIn(true);
    const before = settings;
    setSettings(s => ({ ...s, require_all_marked: value }));
    try {
      await API.put('/daily-subscriptions/settings', { require_all_marked: value });
      swal.success(value
        ? 'Teachers must now mark every student before handing a round in.'
        : 'Teachers can now hand a round in with students still unmarked.');
    } catch (e) {
      setSettings(before);
      swal.error(swal.pickError(e, 'Could not save the setting'));
    } finally { setSavingHandIn(false); }
  };

  const patchRule = (t, patch) =>
    setRules(r => ({ ...r, [t]: { ...(r[t] || {}), ...patch } }));

  const saveRule = async (t) => {
    setSavingType(t);
    try {
      const r = rules[t] || {};
      const out = await API.put(`/daily-subscriptions/charge-rules/${encodeURIComponent(t)}`, {
        charge_when_absent:     r.charge_when_absent ? 1 : 0,
        charge_when_unpaid:     r.charge_when_unpaid ? 1 : 0,
        bill_unmarked_at_close: r.bill_unmarked_at_close ? 1 : 0,
        auto_settle_days:       r.auto_settle_days ? 1 : 0,
        ...(r.auto_settle_from ? { auto_settle_from: r.auto_settle_from } : {}),
      });
      patchRule(t, out.data || {});
      swal.success(`${prettyType(t)} charging rule saved`);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not save the rule'));
    } finally { setSavingType(''); }
  };

  const requireAll = settings?.require_all_marked !== 0;

  if (loading) {
    return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1180, mx: 'auto' }}>
      <ModuleHeader
        icon={<SettingsIcon />}
        title="Collection Settings"
        subtitle="How rounds are handed in, and when a day becomes a debt"
      />

      {error && <Alert severity="error" sx={{ mb: 1.6 }}>{error}</Alert>}

      {/* ══ Handing a round in ═══════════════════════════════════ */}
      <Card elevation={0} sx={{
        p: 2, mb: 1.6, borderRadius: 3,
        bgcolor: '#fff', border: '1px solid #e3eaf3',
      }}>
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            bgcolor: '#2f6fb0', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <SubmitIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#1e3a6b' }}>
              Handing a round in
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
              What a teacher must finish before they can submit for approval.
            </Typography>
          </Box>
          {savingHandIn && <CircularProgress size={18} />}
        </Stack>

        <Divider sx={{ mb: 0.5 }} />

        <SettingRow
          checked={requireAll}
          disabled={savingHandIn}
          onChange={(v) => saveHandIn(v)}
          label="Require every student to be marked before a round can be submitted"
          hint={requireAll
            ? 'ON — a teacher cannot hand a class in while any student is untouched. '
              + 'Every learner must come out as paid, part paid, credit or not eaten. '
              + 'This is the safer rule: a blank is always an omission.'
            : 'OFF — a teacher can hand in what they have walked so far. Unmarked '
              + 'students are simply not part of that submission, and can be marked '
              + 'and submitted later. The screen warns them, and says how many they '
              + 'are leaving out, before anything is sent.'} />

        <Card elevation={0} sx={{
          mt: 1.4, p: 1.4, borderRadius: 2,
          bgcolor: requireAll ? '#f1faf5' : '#fdf8ed',
          border: `1px solid ${requireAll ? '#cfe7d6' : '#f3e0ba'}`,
        }}>
          <Stack direction="row" spacing={1.2}>
            <InfoIcon sx={{ fontSize: 18, color: requireAll ? '#22a559' : '#d99b20', mt: 0.1 }} />
            <Typography sx={{ fontSize: 11, color: '#475569', lineHeight: 1.7 }}>
              {requireAll
                ? 'A class held back by one child at the clinic is the cost of this '
                  + 'setting. If that happens often, turning it off lets the money in '
                  + 'and leaves the child to be marked later.'
                : 'The risk of this setting is a round that is never finished — money '
                  + 'collected for the unmarked students stays with the teacher and '
                  + 'never reaches approval. Check Approve Collections for classes '
                  + 'that keep coming back part-submitted.'}
            </Typography>
          </Stack>
        </Card>
      </Card>

      {/* ══ When a day becomes a debt ════════════════════════════ */}
      <Card elevation={0} sx={{
        p: 2, borderRadius: 3, bgcolor: '#fff', border: '1px solid #e3eaf3',
      }}>
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 1.2 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            bgcolor: '#6b3fa0', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <RuleIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#1e3a6b' }}>
              When a day becomes a debt
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
              Set per collection. School fees are owed whether or not the child came;
              a meal is owed only if it was taken.
            </Typography>
          </Box>
        </Stack>

        {types.length === 0 ? (
          <Alert severity="info" sx={{ fontSize: 12 }}>
            No daily collections are set up yet. Register one under
            <b> Collections → Collection Registration</b> and its rule will appear here.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small" sx={{
              '& th': {
                bgcolor: '#f3f7fc', color: '#334a6b', fontWeight: 800,
                fontSize: 10.5, py: 1,
              },
              '& td': { fontSize: 12, py: 0.8, borderBottom: '1px solid #eef2f7' },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 150 }}>Collection</TableCell>
                  <TableCell sx={{ width: 168 }}>Model</TableCell>
                  <TableCell align="center" sx={{ width: 104 }}>
                    <Tooltip title="Absent, and the day's amount is still owed">
                      <span>Charge when absent</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 104 }}>
                    <Tooltip title="Came, took it, paid nothing — the day's amount is owed">
                      <span>Charge when unpaid</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 104 }}>
                    <Tooltip title="A learner nobody marked is billed when the day is closed">
                      <span>Bill unmarked at close</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 104 }}>
                    <Tooltip title="Settle days nobody closed, automatically — bounded so switching it on cannot post months of back-arrears at once">
                      <span>Settle days by itself</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 84 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {types.map(t => {
                  const r = rules[t] || {};
                  const model = presetOf(r);
                  const busy = savingType === t;
                  return (
                    <TableRow key={t} hover>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {prettyType(t)}
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" fullWidth value={model}
                          SelectProps={{ native: true }}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (PRESETS[v]) patchRule(t, PRESETS[v]);
                          }}
                          sx={{ '& .MuiInputBase-input': { fontSize: 11.5, py: 0.6 } }}>
                          <option value="used">Only when used</option>
                          <option value="compulsory">Compulsory daily fee</option>
                          {model === 'mixed' && <option value="mixed">Custom</option>}
                        </TextField>
                      </TableCell>
                      {['charge_when_absent', 'charge_when_unpaid',
                        'bill_unmarked_at_close', 'auto_settle_days'].map(k => (
                        <TableCell key={k} align="center">
                          <Switch size="small" checked={!!r[k]} disabled={busy}
                            onChange={(e) => patchRule(t, { [k]: e.target.checked ? 1 : 0 })} />
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Button size="small" variant="contained" disableElevation
                          disabled={busy} onClick={() => saveRule(t)}
                          startIcon={busy
                            ? <CircularProgress size={13} color="inherit" />
                            : <SaveIcon sx={{ fontSize: 15 }} />}
                          sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: 11,
                            borderRadius: 1.5, px: 1.4,
                            bgcolor: '#2f6fb0', '&:hover': { bgcolor: '#1e3a6b' },
                          }}>
                          Save
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Card elevation={0} sx={{
          mt: 1.6, p: 1.4, borderRadius: 2,
          bgcolor: '#f2f7fd', border: '1px solid #dce7f5',
        }}>
          <Stack direction="row" spacing={1.2}>
            <InfoIcon sx={{ fontSize: 18, color: '#2f6fb0', mt: 0.1 }} />
            <Box>
              <Typography sx={{ fontSize: 11, color: '#475569', lineHeight: 1.75 }}>
                • <b>Only when used</b> — feeding, transport. Nothing is owed for a
                day the learner did not take it.<br />
                • <b>Compulsory daily fee</b> — daily school fees. The amount is owed
                because the name is on the roll, so it carries forward whether the
                child came or not.<br />
                • Every change is <b>forward-looking</b>. A day already marked keeps
                the charge it was given — that money may already have been receipted
                and banked.
              </Typography>
            </Box>
          </Stack>
        </Card>
      </Card>
    </Box>
  );
}
